import React, { PropsWithChildren } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { useCanAccessEdl } from '../hooks/useCanAccessEdl';
import { ACCESS_EDL_PERMISSION } from '../utils/effectiveAccess.utils';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#efefef]">
    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary" />
  </div>
);

/**
 * Espera GET /me y exige access:edl.
 * Sin el permiso (o sin sesión directory), bloquea la app y ofrece cerrar sesión.
 */
export const EdlAccessGate: React.FC<PropsWithChildren> = ({ children }) => {
  const { logout } = useAuth();
  const { isLoading, directoryError, directorySession } = useUserProfile();
  const canAccessEdl = useCanAccessEdl();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!canAccessEdl) {
    const sessionFailed = Boolean(directoryError) || !directorySession;

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#efefef] gap-4 p-4 max-w-md mx-auto">
        <img
          src="/images/logos/edl_logo_blue.png"
          alt="Earth Data Lab"
          className="h-10 object-contain mb-2"
        />
        <h1 className="text-xl font-semibold text-gray-900 text-center">
          Sin acceso a Earth Data Lab
        </h1>
        <p className="text-center text-gray-700">
          {sessionFailed
            ? 'No se pudo cargar tu sesión de producto. Es posible que tu cuenta no esté registrada en el directorio de usuarios.'
            : `Tu cuenta está autenticada, pero no tiene el permiso ${ACCESS_EDL_PERMISSION} requerido para usar esta aplicación.`}
        </p>
        <p className="text-center text-sm text-gray-500">
          Contacta al administrador para solicitar acceso.
        </p>
        {directoryError && (
          <p className="text-center text-sm text-gray-500 font-mono break-all">
            {directoryError}
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            void logout();
          }}
          className="mt-2 px-4 py-2 bg-secondary text-white rounded shadow-sm hover:bg-secondary/80"
        >
          Cerrar sesión
        </button>
      </div>
    );
  }

  return <>{children}</>;
};
